'use client'

import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { Plane, Mail, ArrowLeft, Shield, GraduationCap, Users, KeyRound, User } from 'lucide-react'
import { Button, Input, Card } from '@/components/ui'

type LoginMode = 'select' | 'admin-email' | 'admin-code' | 'crew'

export default function LoginPage() {
  const router = useRouter()
  const [mode, setMode] = useState<LoginMode>('select')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [identifier, setIdentifier] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  // Dev quick connect
  const [demoLoading, setDemoLoading] = useState<string | null>(null)
  const [showDevPanel, setShowDevPanel] = useState(false)
  const [devPassword, setDevPassword] = useState('')
  const [devUnlocked, setDevUnlocked] = useState(false)
  const [devPasswordError, setDevPasswordError] = useState(false)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Focus first code input when entering code step
  useEffect(() => {
    if (mode === 'admin-code' && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [mode])

  // --- Admin email flow ---
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Échec de l\'envoi du code')

      setMode('admin-code')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    if (value && index === 5) {
      const fullCode = newCode.join('')
      if (fullCode.length === 6) {
        verifyCode(fullCode)
      }
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      verifyCode(pastedData)
    }
  }

  const verifyCode = async (fullCode: string) => {
    setIsLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: fullCode })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Code invalide')

      router.push('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la vérification')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    setError('')
    setIsLoading(true)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Échec du renvoi')

      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du renvoi')
    } finally {
      setIsLoading(false)
    }
  }

  // --- Crew (instructor/student) login flow ---
  const handleCrewLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const res = await fetch('/api/auth/login-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, code: accessCode })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Identifiant ou code incorrect')

      router.push('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la connexion')
    } finally {
      setIsLoading(false)
    }
  }

  // --- Demo login ---
  const handleDemoLogin = async (role: string) => {
    setDemoLoading(role)
    setError('')

    try {
      const res = await fetch('/api/auth/demo-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      })

      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Demo login failed')

      router.push('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Demo login failed')
    } finally {
      setDemoLoading(null)
    }
  }

  const resetToSelect = () => {
    setMode('select')
    setEmail('')
    setCode(['', '', '', '', '', ''])
    setIdentifier('')
    setAccessCode('')
    setError('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-slate-900">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-cyan-500 mb-4">
            <Plane className="h-8 w-8 text-slate-900" />
          </div>
          <h1 className="text-2xl font-bold text-white">Iroise MCC Training</h1>
          <p className="text-slate-400 mt-1">Multi Crew Cooperation Platform</p>
        </div>

        <Card variant="elevated" className="p-6">
          {/* ===== MODE SELECT ===== */}
          {mode === 'select' && (
            <div className="space-y-4">
              <div className="text-center mb-2">
                <h2 className="text-lg font-semibold text-white mb-1">Connexion</h2>
                <p className="text-slate-400 text-sm">Choisissez votre mode de connexion</p>
              </div>

              {/* Crew Login Button (primary - most common) */}
              <button
                onClick={() => setMode('crew')}
                className="w-full flex items-center gap-4 p-4 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-xl transition-colors group"
              >
                <div className="h-12 w-12 rounded-xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <KeyRound className="h-6 w-6 text-cyan-400" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-white group-hover:text-cyan-300 transition-colors">
                    Instructeur / Stagiaire
                  </p>
                  <p className="text-sm text-slate-400">
                    Connexion avec identifiant et code
                  </p>
                </div>
              </button>

              {/* Admin Login Button */}
              <button
                onClick={() => setMode('admin-email')}
                className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl transition-colors group"
              >
                <div className="h-12 w-12 rounded-xl bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-slate-400 group-hover:text-white transition-colors" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-white">
                    Administrateur
                  </p>
                  <p className="text-sm text-slate-400">
                    Connexion par email
                  </p>
                </div>
              </button>
            </div>
          )}

          {/* ===== ADMIN EMAIL STEP ===== */}
          {mode === 'admin-email' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={resetToSelect}
                  className="h-8 w-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 text-slate-400" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">Administrateur</h2>
                  <p className="text-slate-400 text-sm">Entrez votre email pour recevoir un code</p>
                </div>
              </div>

              <form onSubmit={handleSendCode} className="space-y-4">
                <Input
                  type="email"
                  label="Adresse email"
                  placeholder="admin@iroise-aero.fr"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                  error={error}
                />

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={isLoading}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer le code
                </Button>
              </form>
            </>
          )}

          {/* ===== ADMIN CODE STEP ===== */}
          {mode === 'admin-code' && (
            <div className="text-center">
              <div className="h-12 w-12 rounded-full bg-cyan-500/20 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-cyan-400" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-2">
                Code de vérification
              </h2>
              <p className="text-slate-400 text-sm mb-6">
                Un code à 6 chiffres a été envoyé à <span className="text-white">{email}</span>
              </p>

              {/* Code inputs */}
              <div className="flex justify-center gap-2 mb-4" onPaste={handlePaste}>
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => { inputRefs.current[index] = el }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={isLoading}
                    className="w-12 h-14 text-center text-2xl font-mono font-bold bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none disabled:opacity-50"
                  />
                ))}
              </div>

              {error && (
                <p className="text-red-400 text-sm mb-4">{error}</p>
              )}

              <p className="text-slate-500 text-sm mb-4">
                Le code expire dans 10 minutes.
              </p>

              <div className="flex flex-col gap-2">
                <Button
                  variant="ghost"
                  onClick={handleResendCode}
                  disabled={isLoading}
                >
                  Renvoyer le code
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setMode('admin-email')
                    setCode(['', '', '', '', '', ''])
                    setError('')
                  }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Changer d&apos;email
                </Button>
              </div>
            </div>
          )}

          {/* ===== CREW LOGIN (Instructor/Student) ===== */}
          {mode === 'crew' && (
            <>
              <div className="flex items-center gap-3 mb-6">
                <button
                  onClick={resetToSelect}
                  className="h-8 w-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
                >
                  <ArrowLeft className="h-4 w-4 text-slate-400" />
                </button>
                <div>
                  <h2 className="text-lg font-semibold text-white">Instructeur / Stagiaire</h2>
                  <p className="text-slate-400 text-sm">Entrez vos identifiants de connexion</p>
                </div>
              </div>

              <form onSubmit={handleCrewLogin} className="space-y-4">
                <Input
                  label="Identifiant"
                  placeholder="IRO-XXXX"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value.toUpperCase())}
                  required
                  autoFocus
                  className="font-mono tracking-wider"
                />

                <Input
                  label="Code d'accès"
                  type="password"
                  inputMode="numeric"
                  placeholder="000000"
                  value={accessCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setAccessCode(val)
                  }}
                  required
                  className="font-mono tracking-widest"
                />

                {error && (
                  <p className="text-red-400 text-sm">{error}</p>
                )}

                <Button
                  type="submit"
                  className="w-full"
                  isLoading={isLoading}
                  disabled={!identifier || accessCode.length !== 6}
                >
                  <KeyRound className="h-4 w-4 mr-2" />
                  Se connecter
                </Button>
              </form>
            </>
          )}
        </Card>

        {/* Dev Quick Connect - Hidden panel in bottom-right corner */}
        {showDevPanel && (
          <div className="fixed bottom-4 right-4 z-50 w-72">
            <Card variant="elevated" className="p-4 border-slate-700 shadow-2xl">
              {!devUnlocked ? (
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    if (devPassword === '320214') {
                      setDevUnlocked(true)
                      setDevPasswordError(false)
                    } else {
                      setDevPasswordError(true)
                      setDevPassword('')
                    }
                  }}
                  className="space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-slate-400">Accès rapide</p>
                    <button
                      type="button"
                      onClick={() => { setShowDevPanel(false); setDevPassword(''); setDevPasswordError(false) }}
                      className="text-slate-500 hover:text-slate-300 text-xs"
                    >
                      Fermer
                    </button>
                  </div>
                  <Input
                    type="password"
                    inputMode="numeric"
                    placeholder="Code d'accès"
                    value={devPassword}
                    onChange={(e) => { setDevPassword(e.target.value); setDevPasswordError(false) }}
                    autoFocus
                    error={devPasswordError ? 'Code incorrect' : undefined}
                    className="text-sm"
                  />
                  <Button type="submit" variant="secondary" className="w-full text-sm">
                    Valider
                  </Button>
                </form>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
                      <p className="text-xs font-medium text-amber-400">Quick Connect</p>
                    </div>
                    <button
                      onClick={() => { setShowDevPanel(false); setDevUnlocked(false); setDevPassword('') }}
                      className="text-slate-500 hover:text-slate-300 text-xs"
                    >
                      Fermer
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleDemoLogin('admin')}
                      disabled={!!demoLoading}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg text-red-400 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {demoLoading === 'admin' ? (
                        <div className="h-3 w-3 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Shield className="h-3 w-3" />
                      )}
                      Admin
                    </button>
                    <button
                      onClick={() => handleDemoLogin('instructor')}
                      disabled={!!demoLoading}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/30 rounded-lg text-purple-400 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {demoLoading === 'instructor' ? (
                        <div className="h-3 w-3 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <GraduationCap className="h-3 w-3" />
                      )}
                      Instructeur
                    </button>
                    <button
                      onClick={() => handleDemoLogin('student1')}
                      disabled={!!demoLoading}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {demoLoading === 'student1' ? (
                        <div className="h-3 w-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Users className="h-3 w-3" />
                      )}
                      Elève 1
                    </button>
                    <button
                      onClick={() => handleDemoLogin('student2')}
                      disabled={!!demoLoading}
                      className="flex items-center justify-center gap-2 px-3 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 border border-cyan-500/30 rounded-lg text-cyan-400 text-xs font-medium transition-colors disabled:opacity-50"
                    >
                      {demoLoading === 'student2' ? (
                        <div className="h-3 w-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Users className="h-3 w-3" />
                      )}
                      Elève 2
                    </button>
                  </div>
                </div>
              )}
            </Card>
          </div>
        )}

        <div className="text-center text-sm text-slate-500 mt-6 space-y-2">
          <p>Iroise Aéro Formation &copy; {new Date().getFullYear()}</p>
          <div className="flex justify-center gap-4">
            <a href="/privacy" className="hover:text-slate-300 transition-colors">
              Confidentialité
            </a>
            <span>•</span>
            <a href="/terms" className="hover:text-slate-300 transition-colors">
              Conditions d&apos;utilisation
            </a>
          </div>
        </div>
      </div>

      {/* Discreet dev trigger button - bottom right corner */}
      {!showDevPanel && (
        <button
          onClick={() => setShowDevPanel(true)}
          className="fixed bottom-3 right-3 h-8 w-8 rounded-full bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/30 flex items-center justify-center transition-all opacity-30 hover:opacity-100"
          title="Dev"
        >
          <User className="h-3 w-3 text-slate-500" />
        </button>
      )}
    </div>
  )
}
